/**
 * PackHeroStage.module.scss — Toile.
 *
 * Copied from `@product/narcis/packs/PackHeroStage.module.scss` and cut to the showcase: the
 * canvas, trainbar, shimmer, noise plate, ad veil, intro dissolve, status chip, inlet group,
 * connector, error panel, honesty band, auth/consent gate wrappers and the whole CTA bar are gone
 * with the states that drove them. What is kept is the stage box, the pager zones and their
 * hover-only arrow discs, the thumb strip, and the two condition blocks that make the card behave
 * on real devices — `(hover: hover) and (pointer: fine)` and `prefers-reduced-motion`.
 *
 * Retokened onto `--tl-*` (styles/index.scss). Narcis's yolk has no Toile counterpart with hover
 * and dim variants, so the accent's translucent forms are written as literals over
 * rgb(255, 223, 168), which is `--tl-accent`.
 *
 * The one structural change from narcis: the frame is the PACK's ratio, not a fixed 3/4. Narcis
 * caps the portrait with a hardcoded `calc(min(660px, 80vh) * 3 / 4)` and, on desktop, swaps the
 * ratio to 4/4.1 to buy back the fold. Both are the same idea — bound the HEIGHT, derive the width
 * — so this file names that height once (`--tl-hero-h`) and multiplies it by the pack's own aspect
 * ratio, which keeps a wallpaper wide and a coloring page tall while honouring the same budget.
 */

/* ── Stage container ─────────────────────────────────────────────────────────── */

.stage {
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    min-height: 0;

    /* Fallbacks: the component sets all three inline, per selected cell. */
    --tl-stage-ratio: 3 / 4;
    --tl-stage-ar: 0.75;
    --tl-thumb-ratio: 3 / 4;

    /* The height the frame is allowed to take. Everything else derives from it. */
    --tl-hero-h: min(660px, 80vh);

    /* Cap + center the whole stack at every width — otherwise the capped frame hugs one edge (the
       copy edge on desktop, the left edge on tablet). Phones narrower than the cap fill naturally. */
    max-width: min(100%, calc(var(--tl-hero-h) * var(--tl-stage-ar)));
    margin-inline: auto;
}

/* Desktop fold budget (narcis §15, generalized): 49px header + the hero's own top padding (40) +
   the rows that follow the frame (thumb strip ~60 + gaps ≈ 40) = 189px, floored at 380px so a short
   but still ≥640 window scrolls the page rather than crushing the image. svh, never vh — the
   formula's own source unit. */
@media (min-width: 1024px) and (min-height: 640px) {
    .stage {
        --tl-hero-h: max(380px, min(660px, calc(100svh - 189px)));
    }
}

/* ── The frame ───────────────────────────────────────────────────────────────── */

/* The gesture surface: a look's card or the coda, whichever shows. `scroll-margin-top` clears the
   sticky header when the aside's coda door brings the stage into view. */
.frame {
    position: relative;
    width: 100%;
    scroll-margin-top: 64px;
}

/* The coda frame is always mounted (the action inside must mount once) and `hidden` while a look
   shows — this class sets no `display`, so the attribute's own display:none holds. */
.codaSlot {
    width: 100%;
    max-width: min(100%, calc(var(--tl-hero-h) * var(--tl-stage-ar)));
    margin-inline: auto;
}

.bigArea {
    position: relative;
    width: 100%;
    max-width: min(100%, calc(var(--tl-hero-h) * var(--tl-stage-ar)));
    aspect-ratio: var(--tl-stage-ratio);
    border-radius: 16px;
    overflow: hidden;
    border: 1px solid var(--tl-border);
    background: var(--tl-surface);
    margin-inline: auto;
}

.bigImage {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    z-index: 1;
}

/* ── Card as pager ───────────────────────────────────────────────────────────── */
/* The prev zone takes the left 28% of the card because the dominant gesture is "forward" — the
   wide majority surface goes to it, the way a comic reader pages by tapping anywhere but the back
   edge. z-index 5 keeps the zones above the image and below the corner tag (which is
   pointer-events:none anyway). */

.pager {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
}

.pagerZone {
    appearance: none;
    border: none;
    background: none;
    padding: 0 14px;
    margin: 0;
    cursor: pointer;
    display: flex;
    align-items: center;

    &:focus-visible {
        outline: 2px solid var(--tl-accent);
        outline-offset: -3px;
        border-radius: 16px;
    }
}

.pagerZonePrev {
    flex: 0 0 28%;
    justify-content: flex-start;
}

.pagerZoneNext {
    flex: 1 1 auto;
    justify-content: flex-end;
}

/* Feedback, not chrome: the discs exist only while a fine pointer is over the card (or a zone holds
   keyboard focus). display:none is the base state so a touch device never renders them at all. */
.pagerArrow {
    display: none;
    width: 34px;
    height: 34px;
    border-radius: 9999px;
    background: rgba(20, 18, 16, 0.72);
    color: var(--tl-text);
    font-size: 16px;
    line-height: 1;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(6px);
}

@media (hover: hover) and (pointer: fine) {
    .pagerArrow {
        display: flex;
        opacity: 0;
        transition: opacity 160ms ease;
    }

    .bigArea:hover .pagerArrow,
    .pagerZone:focus-visible .pagerArrow {
        opacity: 1;
    }
}

/* ── Corner tag (bottom-right) ───────────────────────────────────────────────── */

.cornerTag {
    position: absolute;
    bottom: 12px;
    right: 12px;
    z-index: 6;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    letter-spacing: 0.08em;
    color: var(--tl-text);
    background: rgba(20, 18, 16, 0.82);
    padding: 5px 11px;
    border-radius: 9999px;
    backdrop-filter: blur(6px);
    pointer-events: none;
}

/* The look door takes the bottom-right corner (`core/packs/stage` LookDoor); the tag moves left and
   yields the width the door needs (~150px with its gaps) — a long READING · STYLE pair ellipsizes
   rather than running under the door at 390. */
.cornerTagLeft {
    right: auto;
    left: 12px;
    max-width: calc(100% - 172px);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ── Nine-thumb strip ────────────────────────────────────────────────────────── */

.thumbStrip {
    display: flex;
    gap: 6px;
    align-items: stretch;
    list-style: none;
    padding: 0;
    margin: 0;
}

/* The strip's slot geometry, shared by the nine thumbs and the coda thumb (whose ghost look is
   core's `CodaThumb`); `.thumb` adds the picture thumb's own border and ground. */
.thumb,
.thumbSlot {
    flex: 1;
    position: relative;
    aspect-ratio: var(--tl-thumb-ratio);
    border-radius: 4px;
    overflow: hidden;
    min-width: 0;
}

.thumb {
    padding: 0;
    border: 1px solid var(--tl-border);
    background: var(--tl-surface);
    cursor: pointer;
    display: block;

    &:hover {
        border-color: var(--tl-border-strong);
    }

    &:focus-visible {
        outline: 2px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

.thumbActive {
    border-color: var(--tl-accent);
    box-shadow: 0 0 0 1px rgba(255, 223, 168, 0.9), 0 0 18px rgba(255, 223, 168, 0.25);
}

.thumbImage {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    z-index: 1;
}

/* ── Responsive ──────────────────────────────────────────────────────────────── */
/* The strip fits all ten at 390 — nine thumbs and the coda — so the set's end, and what follows
   it, is seen before any swipe (plan 2026_09_23_pack_stage_coda.md § Decided 2). No scroll, no
   fixed thumb width: each slot takes a tenth of the row. */

@media (max-width: 639px) {
    .thumbStrip {
        gap: 5px;
    }

    .thumb,
    .thumbSlot {
        border-radius: 3px;
    }
}

/* ── Reduced motion ──────────────────────────────────────────────────────────── */
/* The pager discs still appear on hover — they are the only signal that the card pages at all.
   Only the fade is dropped. */

@media (prefers-reduced-motion: reduce) {
    .pagerArrow {
        transition: none;
    }
}
